/**
 * Homestead build mode — the hammer. A translucent ghost of the selected
 * catalog piece hovers at the aim point; it SNAPS one of its sockets onto a
 * nearby placed piece's socket, else places fully free (the snap-points +
 * full-free decision). All poses PLANET-LOCAL — the ghost parents to the planet
 * group and the committed transform goes to the server verbatim (which
 * validates permission/containment/budget/cost).
 *
 * ⚠ SNAP SELECTION IS AIM-LED (rewritten 2026-08-04). The original rule took
 * the globally minimum-distance (ghostSocket, placedSocket) pair, which is
 * unpredictable in practice: a foundation carries EIGHTEEN sockets, several sit
 * inside the 0.9 m tolerance at once, and the winning pair flips on sub-degree
 * aim changes with nothing on screen to explain why. Now:
 *   1. candidates are TYPE-GATED (corner↔corner, edge↔edge; face centres are
 *      wildcards) — a corner welded to an edge midpoint is a half-unit offset,
 *      never what anyone meant;
 *   2. they are ranked by how close the WELD POINT is to where you are LOOKING,
 *      not by a joint minimum — you get the socket under the crosshair;
 *   3. every candidate is DRAWN (snapMarkers), the live one hot — the snap is
 *      visible before you commit to it;
 *   4. T cycles through them and V drops to free placement, so a snap you don't
 *      want is one keystroke away instead of a fight with the aim.
 *
 * Pure helpers (snapCandidates / bestSnap / surfaceAlignQuat / socketsOf) are
 * exported for node tests; the class is the thin THREE + input shell main.ts
 * drives per frame.
 */
import * as THREE from "three";
import { PIECE_CATALOG, type Quat, type Vec3 } from "@hyperlight/shared";
import { compiledPieces, pieceInstances } from "./meshes.js";
import { buildGeometry } from "../world/settlement/buildingMeshes.js";

/** Snap search tolerance (metres the ghost may be nudged to make a weld). */
export const SNAP_TOL_M = 0.9;
/** Ghost reach: metres ahead of the camera the build target sits. */
export const BUILD_REACH_M = 7;
/** Demolish reach: metres from the PLAYER the crosshair pick may reach. */
export const DEMOLISH_REACH_M = 9;
/** A held weld survives a rival this much nearer the aim (anti-flicker). */
export const SNAP_HYSTERESIS_M = 0.35;
/** Weld points closer together than this are the SAME weld (shared corners:
 *  four floors meeting at a post offer four identical sockets). */
const WELD_EPS_M = 0.02;

/**
 * Snap-socket classes, read off the catalog's `prefix:name` socket naming
 * (pieces.ts). `top:` / `ground:` face centres are WILDCARDS — they are the
 * "rest this on that" sockets (ridge cap onto a roof ridge, post under a beam)
 * and legitimately meet edges and corners.
 */
export type SocketKind = "corner" | "edge" | "face";

/** Class of a compiled socket name (`corner:bl` → corner, `top:c` → face). */
export function socketKind(name: string): SocketKind {
  if (name.startsWith("corner")) return "corner";
  if (name.startsWith("edge")) return "edge";
  return "face";
}

/** May these two socket classes weld? Faces match anything; corner/edge must
 *  match exactly (the half-unit-offset guard). */
export function socketsCompatible(a: SocketKind, b: SocketKind): boolean {
  return a === "face" || b === "face" || a === b;
}

/** A snap socket in some frame, with the class that gates its welds. */
export interface SnapSocket { p: Vec3; k: SocketKind }

/** Doc-space snap sockets of catalog piece `t` (compiled, cached). */
export function socketsOf(t: number): SnapSocket[] {
  const entry = PIECE_CATALOG[t];
  const compiled = entry ? compiledPieces().get(entry.id) : undefined;
  if (!compiled) return [];
  return compiled.sockets.map((s) => ({
    p: [s.position[0], s.position[1], s.position[2]] as Vec3,
    k: socketKind(s.name),
  }));
}

/** v rotated by q, plus offset o. */
export function poseSocket(v: Vec3, q: Quat, o: Vec3): Vec3 {
  const [qx, qy, qz, qw] = q;
  const tx = 2 * (qy * v[2] - qz * v[1]);
  const ty = 2 * (qz * v[0] - qx * v[2]);
  const tz = 2 * (qx * v[1] - qy * v[0]);
  return [
    v[0] + qw * tx + qy * tz - qz * ty + o[0],
    v[1] + qw * ty + qz * tx - qx * tz + o[1],
    v[2] + qw * tz + qx * ty - qy * tx + o[2],
  ];
}

function sqDist(a: Vec3, b: Vec3): number {
  const dx = a[0] - b[0], dy = a[1] - b[1], dz = a[2] - b[2];
  return dx * dx + dy * dy + dz * dz;
}

/** A snap solution: the translation carrying the ghost onto the weld, plus the
 *  pair that made it — `gi` = ghost socket index, `weld` = the placed socket
 *  (the WELD POINT rotation pivots around while snapped), `aimD` = how far that
 *  weld sits from the aim point (the ranking key). */
export interface SnapHit {
  d: Vec3;
  gi: number;
  weld: Vec3;
  aimD: number;
}

/** Are two weld points the same physical corner? */
export function sameWeld(a: Vec3, b: Vec3): boolean {
  return Math.abs(a[0] - b[0]) < WELD_EPS_M
    && Math.abs(a[1] - b[1]) < WELD_EPS_M
    && Math.abs(a[2] - b[2]) < WELD_EPS_M;
}

/**
 * Every weld the ghost could make from a free placement at `aim`, BEST FIRST.
 *
 * `ghostSockets` are rotated about the piece origin but NOT translated (the
 * free pose puts the origin at `aim`); `placedSockets` are planet-local. For
 * each placed socket the compatible ghost socket needing the smallest nudge
 * wins, and the candidate survives if that nudge is within `tol` — so the piece
 * always lands close to where you aimed.
 *
 * Ordering key is |weld − aim|: the socket nearest the crosshair, i.e. the one
 * you can SEE yourself pointing at. (Ranking by the nudge distance instead —
 * the old rule — makes the winner a joint minimum over ~18 × 8 pairs, which
 * jumps discontinuously as you turn.) Co-located sockets collapse to one entry
 * so a corner shared by four floors isn't four identical cycle stops.
 */
export function snapCandidates(aim: Vec3, ghostSockets: readonly SnapSocket[],
  placedSockets: readonly SnapSocket[], tol = SNAP_TOL_M): SnapHit[] {
  const out: SnapHit[] = [];
  const tol2 = tol * tol;
  for (const placed of placedSockets) {
    const p = placed.p;
    let gi = -1;
    let bestD2 = tol2;
    let bd: Vec3 = [0, 0, 0];
    for (let i = 0; i < ghostSockets.length; i++) {
      const g = ghostSockets[i];
      if (!socketsCompatible(g.k, placed.k)) continue;
      const dx = p[0] - (aim[0] + g.p[0]);
      const dy = p[1] - (aim[1] + g.p[1]);
      const dz = p[2] - (aim[2] + g.p[2]);
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 <= bestD2) { bestD2 = d2; gi = i; bd = [dx, dy, dz]; }
    }
    if (gi < 0) continue;
    const ax = p[0] - aim[0], ay = p[1] - aim[1], az = p[2] - aim[2];
    const hit: SnapHit = { d: bd, gi, weld: [p[0], p[1], p[2]], aimD: Math.sqrt(ax * ax + ay * ay + az * az) };
    const dupe = out.find((o) => sameWeld(o.weld, hit.weld));
    if (dupe) {
      // Same physical weld from another piece — keep the smaller nudge.
      const dd = dupe.d[0] * dupe.d[0] + dupe.d[1] * dupe.d[1] + dupe.d[2] * dupe.d[2];
      if (bestD2 < dd) { dupe.d = hit.d; dupe.gi = hit.gi; }
      continue;
    }
    out.push(hit);
  }
  out.sort((a, b) => (a.aimD - b.aimD)
    || ((a.d[0] * a.d[0] + a.d[1] * a.d[1] + a.d[2] * a.d[2])
      - (b.d[0] * b.d[0] + b.d[1] * b.d[1] + b.d[2] * b.d[2])));
  return out;
}

/**
 * The weld to take this frame: the aim-nearest candidate, EXCEPT that a weld
 * already held survives while nothing else is SNAP_HYSTERESIS_M nearer the aim
 * — the ghost must not trade welds on a twitch. Pure; null = free placement.
 */
export function bestSnap(aim: Vec3, ghostSockets: readonly SnapSocket[],
  placedSockets: readonly SnapSocket[], tol = SNAP_TOL_M, held?: Vec3 | null): SnapHit | null {
  const list = snapCandidates(aim, ghostSockets, placedSockets, tol);
  if (list.length === 0) return null;
  if (held) {
    const keep = list.find((c) => sameWeld(c.weld, held));
    if (keep && keep.aimD <= list[0].aimD + SNAP_HYSTERESIS_M) return keep;
  }
  return list[0];
}

/** Quaternion aligning local +y to `up`, then yawed about `up` (piece stance). */
export function surfaceAlignQuat(up: Vec3, yaw: number): Quat {
  const q = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0), new THREE.Vector3(up[0], up[1], up[2]));
  const y = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(up[0], up[1], up[2]), yaw);
  y.multiply(q);
  return [y.x, y.y, y.z, y.w];
}

/** What the ghost is showing this frame (the exact transform a place commits). */
export interface GhostPose {
  t: number;
  p: Vec3;
  q: Quat;
  mat: number;
  snapped: boolean;
  /** The weld point (planet-local) when snapped — the CONNECTION the reticle marks. */
  weld: Vec3 | null;
}

/** Rotation axes the scroll wheel spins about, cycled by R. Local to the
 *  surface-aligned piece frame: yaw = about the surface normal, pitch/roll =
 *  about the piece's local x/z. */
export const ROTATE_AXES = ["YAW", "PITCH", "ROLL"] as const;
const AXIS_VEC: readonly [number, number, number][] = [[0, 1, 0], [1, 0, 0], [0, 0, 1]];
/** Radians per scroll notch / rotate step. */
export const ROTATE_STEP = Math.PI / 12;

/** Aiming this far from the held weld point releases the pivot. */
export const WELD_RELEASE_M = 2.0;

/** Marker sizes: every candidate weld gets a dot, the live one a bigger one. */
const MARK_R = 0.075;
const MARK_LIVE_SCALE = 2.1;
/** Never draw more than this many candidate dots (clutter guard). */
const MARK_MAX = 12;

export class HomesteadBuild {
  active = false;
  /** Selected catalog index — NULL = empty hands (build mode starts this way;
   *  Q puts the held piece away; demolish requires empty hands). */
  sel: number | null = null;
  /** Which axis scroll rotates about (index into ROTATE_AXES; R cycles). */
  axis = 0;
  /** Palette tint choice (Piece.mat). */
  matChoice = 0;
  /** Accumulated piece-local spin (scroll applies ROTATE_STEP about the axis). */
  private spinQ = new THREE.Quaternion();
  private alignQ = new THREE.Quaternion();
  private axisV = new THREE.Vector3();
  private stepQ = new THREE.Quaternion();

  private ghost: THREE.Group | null = null;
  private ghostT = -1;
  private ghostMat = new THREE.MeshBasicMaterial({
    color: 0x2fd8ff, transparent: true, opacity: 0.4, depthWrite: false,
  });
  private pose: GhostPose | null = null;
  /** The held weld: the placed socket the ghost pivots around while snapped.
   *  `pinned` = chosen deliberately with T, so the aim heuristic leaves it alone. */
  private weld: { point: Vec3; ghostIdx: number; sel: number; pinned: boolean } | null = null;
  private valid = true;
  /** V: snapping off = FREE PLACEMENT, the escape hatch for the one placement
   *  the socket grid can't express (and the fastest way to stop fighting it). */
  snapOn = true;
  /** Last frame's weld candidates, aim-nearest first — what the markers draw
   *  and what T cycles through. */
  private candidates: SnapHit[] = [];
  /** Structural validity of the current placement (main computes it from the
   *  shared support graph) — tints the ghost cyan (ok) / red (would float). */
  setValid(ok: boolean): void {
    if (ok === this.valid) return;
    this.valid = ok;
    this.ghostMat.color.setHex(ok ? 0x2fd8ff : 0xff5a4a);
  }

  /** Arm the hammer with catalog piece `t`. A FRESH selection always starts
   *  STRAIGHT-UP relative to the ground: accumulated spin resets to the pure
   *  surface alignment (and any held weld releases). */
  select(t: number): void {
    this.sel = t;
    this.spinQ.identity();
    this.weld = null;
  }

  /** Cycle the selected piece (wraps; from empty hands it picks the first). */
  cycle(dir: 1 | -1): string {
    const next = this.sel === null
      ? 0
      : (this.sel + dir + PIECE_CATALOG.length) % PIECE_CATALOG.length;
    this.select(next);
    return PIECE_CATALOG[next].name;
  }

  /** Q: put the held piece away — empty hands arm the demolish flow. */
  putDown(): void {
    this.sel = null;
    this.weld = null;
    this.hide();
  }

  /** Spin the ghost one step about the current axis (scroll up/down = ±1),
   *  applied in the piece's LOCAL frame so stacked rotations feel physical. */
  rotate(dir: 1 | -1): void {
    const [x, y, z] = AXIS_VEC[this.axis];
    this.stepQ.setFromAxisAngle(this.axisV.set(x, y, z), dir * ROTATE_STEP);
    this.spinQ.multiply(this.stepQ);
  }

  /** Cycle which axis scroll rotates about (R). Returns the axis name for the HUD. */
  cycleAxis(): string {
    this.axis = (this.axis + 1) % ROTATE_AXES.length;
    return ROTATE_AXES[this.axis];
  }

  /** Reset accumulated spin to surface-flat (handy when a piece is hopelessly askew). */
  resetSpin(): void {
    this.spinQ.identity();
  }

  /** V: toggle snapping. Off releases the held weld and places free — returns
   *  the new state for the HUD. */
  toggleSnap(): boolean {
    this.snapOn = !this.snapOn;
    if (!this.snapOn) this.weld = null;
    return this.snapOn;
  }

  /**
   * T: step to the NEXT weld candidate under the aim and PIN it, so a snap you
   * didn't want costs one keystroke instead of a fight with the mouse. Returns
   * how many candidates there are (0 = nothing to cycle).
   */
  cycleSnap(): number {
    const list = this.candidates;
    if (this.sel === null || list.length === 0) return 0;
    const held = this.weld;
    const at = held ? list.findIndex((c) => sameWeld(c.weld, held.point)) : -1;
    const next = list[(at + 1) % list.length];
    this.weld = { point: [...next.weld] as Vec3, ghostIdx: next.gi, sel: this.sel, pinned: true };
    return list.length;
  }

  /** Weld candidates under the aim this frame (aim-nearest first) — the HUD's
   *  "1/4 SNAP" readout and the marker source. */
  snapChoices(): readonly SnapHit[] {
    return this.candidates;
  }

  /** The transform a place would commit right now (null until update ran). */
  placement(): GhostPose | null {
    return this.pose;
  }

  /**
   * Drive the ghost for this frame. All inputs PLANET-LOCAL:
   *  - `target` — aim point (camera ray at BUILD_REACH, ground-clamped by caller)
   *  - `up`     — surface normal at the target
   *  - `placedSockets` — snap candidates from nearby placed pieces
   * Parents the ghost under `parent` (the planet group) and returns the pose —
   * null with empty hands (sel === null).
   *
   * WELD PIVOT: once snapped, the weld point (the placed socket) is HELD while
   * the aim stays within WELD_RELEASE of it — rotation then orbits the piece
   * AROUND the weld instead of its own origin (the Valheim feel). Aiming away
   * releases it and the aim-led snap resumes.
   *
   * Candidates are recomputed EVERY frame even while a weld is held, so the
   * markers stay live and T always has the current list to cycle.
   */
  update(parent: THREE.Object3D, target: Vec3, up: Vec3,
    placedSockets: readonly SnapSocket[]): GhostPose | null {
    const sel = this.sel;
    if (sel === null) { this.hide(); return null; }
    this.ensureGhost(parent, sel);
    // Final pose = surface alignment ∘ accumulated local spin.
    this.alignQ.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(up[0], up[1], up[2]));
    this.alignQ.multiply(this.spinQ);
    const q: Quat = [this.alignQ.x, this.alignQ.y, this.alignQ.z, this.alignQ.w];
    // Ghost sockets rotated about the origin (translation applied after).
    const rotated: SnapSocket[] = socketsOf(sel).map((s) => ({ p: poseSocket(s.p, q, [0, 0, 0]), k: s.k }));
    this.candidates = this.snapOn ? snapCandidates(target, rotated, placedSockets) : [];
    let p: Vec3;
    let snapped = false;
    const w = this.snapOn ? this.weld : null;
    const nearWeld = w !== null && w.sel === sel && w.ghostIdx < rotated.length
      && sqDist(target, w.point) <= WELD_RELEASE_M * WELD_RELEASE_M;
    if (w && nearWeld) {
      // Pivot: translate so the held ghost socket sits exactly on the weld.
      const gs = rotated[w.ghostIdx].p;
      p = [w.point[0] - gs[0], w.point[1] - gs[1], w.point[2] - gs[2]];
      snapped = true;
      // A held weld yields once a rival is clearly nearer the aim (hysteresis)
      // — unless T PINNED it, in which case the player's explicit choice beats
      // the aim heuristic until they look away (releasing the weld entirely).
      const rival = w.pinned ? null : this.candidates[0];
      if (rival && !sameWeld(rival.weld, w.point)) {
        const heldD = this.candidates.find((c) => sameWeld(c.weld, w.point))?.aimD ?? Infinity;
        if (rival.aimD + SNAP_HYSTERESIS_M < heldD) {
          p = [target[0] + rival.d[0], target[1] + rival.d[1], target[2] + rival.d[2]];
          this.weld = { point: [...rival.weld] as Vec3, ghostIdx: rival.gi, sel, pinned: false };
        }
      }
    } else {
      this.weld = null;
      p = [target[0], target[1], target[2]];
      const snap = this.snapOn
        ? bestSnap(target, rotated, placedSockets, SNAP_TOL_M, null) : null;
      if (snap) {
        p = [p[0] + snap.d[0], p[1] + snap.d[1], p[2] + snap.d[2]];
        this.weld = { point: snap.weld, ghostIdx: snap.gi, sel, pinned: false };
        snapped = true;
      }
    }
    const g = this.ghost!;
    g.position.set(p[0], p[1], p[2]);
    g.quaternion.set(q[0], q[1], q[2], q[3]);
    g.visible = this.active;
    this.pose = { t: sel, p, q, mat: this.matChoice, snapped, weld: this.weld ? [...this.weld.point] as Vec3 : null };
    this.updateMarkers(parent);
    return this.pose;
  }

  hide(): void {
    if (this.ghost) this.ghost.visible = false;
    this.candidates = [];
    this.updateMarkers(null);
    this.pose = null;
  }

  // ── weld markers: the snap you are about to make, ON SCREEN ────────────────
  // The single biggest readability fix. Every candidate weld under the aim gets
  // a dot; the one the ghost is actually using burns bright and larger. Drawn
  // depthTest-free so a weld inside a wall is still visible — you are choosing
  // a CONNECTION, and you cannot choose what you cannot see.
  private markerGroup: THREE.Group | null = null;
  private markerGeo: THREE.BufferGeometry | null = null;
  private markerDim = new THREE.MeshBasicMaterial({
    color: 0x2fd8ff, transparent: true, opacity: 0.4, depthTest: false, depthWrite: false,
  });
  private markerLive = new THREE.MeshBasicMaterial({
    color: 0xfff2a8, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false,
  });

  private updateMarkers(parent: THREE.Object3D | null): void {
    const show = parent !== null && this.active && this.snapOn && this.candidates.length > 0;
    if (!show) {
      if (this.markerGroup) this.markerGroup.visible = false;
      return;
    }
    if (!this.markerGeo) this.markerGeo = new THREE.OctahedronGeometry(MARK_R, 0);
    if (!this.markerGroup || this.markerGroup.parent !== parent) {
      this.markerGroup?.parent?.remove(this.markerGroup);
      this.markerGroup = new THREE.Group();
      this.markerGroup.renderOrder = 3;
      parent.add(this.markerGroup);
    }
    const group = this.markerGroup;
    const held = this.weld?.point ?? null;
    const n = Math.min(this.candidates.length, MARK_MAX);
    while (group.children.length < n) {
      const m = new THREE.Mesh(this.markerGeo, this.markerDim);
      m.renderOrder = 3;
      group.add(m);
    }
    for (let i = 0; i < group.children.length; i++) {
      const m = group.children[i] as THREE.Mesh;
      if (i >= n) { m.visible = false; continue; }
      const c = this.candidates[i];
      const live = held !== null && sameWeld(c.weld, held);
      m.visible = true;
      m.material = live ? this.markerLive : this.markerDim;
      m.scale.setScalar(live ? MARK_LIVE_SCALE : 1);
      m.position.set(c.weld[0], c.weld[1], c.weld[2]);
    }
    group.visible = true;
  }

  private disposeMarkers(): void {
    this.markerGroup?.parent?.remove(this.markerGroup);
    this.markerGroup = null;
    this.markerGeo?.dispose();
    this.markerGeo = null;
  }

  /** (Re)build the translucent ghost when selection or parent changed. */
  private ensureGhost(parent: THREE.Object3D, sel: number): void {
    if (this.ghost && this.ghostT === sel && this.ghost.parent === parent) return;
    this.disposeGhost();
    const group = new THREE.Group();
    // Expand the piece at the ORIGIN (identity pose) — the group carries the pose.
    const instances = pieceInstances([{ t: sel, p: [0, 0, 0], q: [0, 0, 0, 1], mat: 0 }]);
    const mtx = new THREE.Matrix4();
    for (const inst of instances) {
      const mesh = new THREE.Mesh(buildGeometry(inst), this.ghostMat);
      mtx.fromArray(inst.matrix);
      mesh.applyMatrix4(mtx);
      group.add(mesh);
    }
    group.visible = false;
    parent.add(group);
    this.ghost = group;
    this.ghostT = sel;
  }

  private disposeGhost(): void {
    if (!this.ghost) return;
    this.ghost.parent?.remove(this.ghost);
    this.ghost.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    this.ghost = null;
  }

  dispose(): void {
    this.disposeGhost();
    this.disposeMarkers();
    this.ghostMat.dispose();
    this.markerDim.dispose();
    this.markerLive.dispose();
    this.candidates = [];
    this.pose = null;
    this.active = false;
  }
}
